/**
 * The designed loading and empty states — `02/ Inquiries/ Bulk + States` (12:10921).
 *
 * The brief is explicit: "designed empty states, skeleton loading … the states exist in
 * Figma; build them, not spinners." So both are built from their nodes:
 *
 *  · `Table/SkeletonRow` (6:328) — h-48, px-16, 24px gaps, a 16px `radius` 4 block then
 *    bars of 180/100/120/64 at h-10 `radius/pill`, ALL filled `border/default`. Note the
 *    fill: the design uses the border colour for skeleton bars, not `bg/subtle`, which is
 *    why this does not reuse the shared `SkeletonRows` primitive.
 *  · `Table/Empty` (12:11359) — a 480px card with a DASHED `border/default`, a 5×3 dot
 *    grid, centred `Body/14` copy in `text/secondary`, and an `accent/indigo` CTA.
 *
 * ===========================================================================
 * THE EMPTY STATE IS STAGE-AWARE, AND THAT IS THE DESIGN'S OWN IDEA
 * ===========================================================================
 * Figma's copy is not generic. It reads "No inquiries in Advance Pending — follow up on
 * 3 Pending Confirmation inquiries", with the CTA "View Pending Confirmation": an empty
 * column points at the column UPSTREAM of it and says how many are waiting there. That
 * pattern is generalised here over the ratified board order rather than hard-coding one
 * sentence, and it needs no new copy for four of the five stages.
 *
 * Two variants extend it, because the design authored only the stage case and a table
 * can be empty for two other reasons:
 *  · a FILTERED empty ("nothing matches this search") offers to clear the filters —
 *    telling someone to "follow up on Pending Confirmation" when they typed a typo into
 *    the search box would be nonsense;
 *  · a TRULY empty country offers "New Inquiry".
 * Both reuse the same card, the same dot grid and the same CTA. No new colour, no new
 * geometry, no new component.
 *
 * ASSET NOTE (design-to-code G5). The dot grid exports as two PNG rows. It is drawn in
 * CSS instead, for the reason already recorded twice in this codebase
 * (`components/ui/Checkbox.tsx`, `components/ui/StagePill.tsx`): the export URLs expire
 * in ~7 days, so committing them ships a link that breaks. The geometry is preserved
 * exactly — 5 dots of 6px on a 70px row, 10px gaps, 3 rows — and the colours become
 * tokens (`border/default`, with the one highlighted dot on `accent/indigo`) instead of
 * hexes baked into a bitmap.
 *
 * ===========================================================================
 * PHASE 2.5a · L5 — THE SPOT REPLACES THE DOT GRID, AND ONLY WHERE ASKED
 * ===========================================================================
 * An AUTHORED divergence from `Table/Empty`: the three empty states this page can reach
 * now draw a hand-rolled `Spot` instead of the Figma dot grid, because a 5×3 grid of grey
 * dots reads as a loading placeholder that stopped rather than as a state with something
 * to say. The dot grid is not deleted and is still the DEFAULT of `InquiryEmptyCard` —
 * see the `spot` prop's own note for why that matters to callers outside this file.
 *
 * The geometry, the dashed card, the CTA and the stage-aware copy are all untouched. What
 * changes is the mark, and one button label (recorded at its own branch below).
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { BOARD_STAGES, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { Spot } from '../../components/ui';
import type { SpotName } from '../../components/ui';
import { cn } from '../../lib/cn';

/* ──────────────────────────────────────────────────────────────── skeleton ── */

function SkeletonBar({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse bg-line motion-reduce:animate-none', className)}
    />
  );
}

/** `SkeletonTable` (12:11340): a `radius/md` card of `Table/SkeletonRow`s. */
export function InquirySkeletonTable({ rows = 6 }: { rows?: number }): React.JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading inquiries"
      className="overflow-hidden rounded-md border border-line bg-surface"
    >
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="crf-register-row flex items-center gap-6 border-b border-line px-4 last:border-b-0"
        >
          <SkeletonBar className="size-4 shrink-0 rounded-[4px]" />
          <SkeletonBar className="h-2.5 w-[180px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-[100px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-[120px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-16 rounded-pill" />
        </div>
      ))}
    </div>
  );
}

/** The board's own loading state: the same bars, in card-shaped blocks. */
export function BoardColumnSkeleton(): React.JSX.Element {
  return (
    <div aria-busy="true" aria-label="Loading cards" className="flex flex-col gap-2">
      {Array.from({ length: 3 }, (_unused, index) => (
        <div
          key={index}
          className="flex flex-col gap-2 rounded-md border border-line bg-surface p-3"
        >
          <SkeletonBar className="h-2.5 w-[140px] rounded-pill" />
          <SkeletonBar className="h-2.5 w-[90px] rounded-pill" />
          <div className="flex items-center justify-between pt-1">
            <SkeletonBar className="h-5 w-[70px] rounded-pill" />
            <SkeletonBar className="size-6 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── dot grid ── */

/**
 * The `dot-grid` mark (I12:11359;6:422). 3 rows × 5 dots, 6px each, 10px gaps.
 *
 * `highlight` is the index of the indigo dot — the middle one (row 1, dot 2) in the
 * design. Passing `-1` renders the grid with no highlight.
 */
function DotGrid({ highlight = 7 }: { highlight?: number }): React.JSX.Element {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2.5">
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex h-1.5 w-[70px] items-center gap-2.5">
          {[0, 1, 2, 3, 4].map((column) => {
            const index = row * 5 + column;
            return (
              <span
                key={column}
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  index === highlight ? 'bg-indigo' : 'bg-line',
                )}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── empty ── */

/**
 * `Table/Empty` (12:11359). Dashed `border/default`, `radius/md`, p-40, 16px gaps.
 *
 * The CTA is a `<Link>` when it navigates and a `<button>` when it acts, because the
 * design's single "cta" node is used for both jobs across the file and only one of those
 * is a link. Anything navigational must be a real anchor: middle-click and
 * copy-link-address are how a coordinator opens the upstream stage in a second tab.
 */
export function InquiryEmptyCard({
  message,
  cta,
  spot,
  className,
}: {
  message: string;
  cta?: ReactNode;
  /**
   * L5. The hand-rolled inline mark that REPLACES the dot grid — not an addition
   * beside it. Two drawings stacked above one sentence would be an illustration
   * contest; the spot exists so the card does not read as an error, and one mark
   * does that.
   *
   * Optional, and absent means the Figma dot grid, because this card has callers
   * outside this file (`Timeline.tsx` draws it for an empty follow-up list and an
   * empty activity feed). Making the spot the default would have silently
   * restyled two states in a file that is not in this task's scope; making it a
   * prop leaves those callers byte-identical and leaves the seam open — wiring
   * the `activity` spot there is one word, whenever that file is in scope.
   */
  spot?: SpotName;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[480px] flex-col items-center justify-center gap-4 rounded-md border border-dashed border-line bg-surface p-10',
        className,
      )}
    >
      {/*
       * `text/muted` — the quietest foreground the token set has, and a step above
       * the dot grid's `border/default` it replaces. Measured 2.63:1 on `bg/surface`
       * in light and 6.26:1 in dark; both are BELOW the 4.5:1 text floor and that is
       * correct, because this is not text and not a control. It is `aria-hidden`
       * decoration whose entire meaning is carried by the sentence and the button
       * beneath it, which is the "pure decoration" exception in WCAG 1.4.11. Nothing
       * is learned from the drawing that is not also written.
       */}
      {spot === undefined ? <DotGrid /> : <Spot name={spot} className="size-12 text-fg-muted" />}
      <p className="max-w-[220px] text-center text-body text-fg-secondary">{message}</p>
      {cta}
    </div>
  );
}

/* WT5 — ledger item 11: this constant was the last INQUIRIES carrier of the
 * retired 4.22 primary (`bg-indigo` + the theme-dependent `hover:bg-indigo/90`
 * alpha fade). The empty-state CTA was rebuilt in this cluster (button → link,
 * the kill ruling), so the fill dies with it per the filing: the D1a authored
 * trio, contrast RISING through the states, no opacity modifier on the fill. */
const CTA_CLASSES =
  'inline-flex h-9 items-center justify-center rounded-sm bg-indigo-strong px-3.5 text-body font-medium text-fg-inverse hover:bg-indigo-strong-hover active:bg-indigo-strong-active';

/**
 * The stage upstream of `stage` in the ratified board order — the one whose rows feed it.
 *
 * `lost` has none: nothing "flows into" Lost from one particular column, so an empty
 * Lost column gets the plain message and no cross-link. Returning a wrong upstream there
 * would send a coordinator to chase a queue that has nothing to do with the empty one.
 */
export function upstreamStage(stage: InquiryStage): InquiryStage | null {
  if (stage === 'lost') return null;
  const order = BOARD_STAGES.filter((candidate) => candidate !== 'lost');
  const index = order.indexOf(stage);
  if (index <= 0) return null;
  return order[index - 1] ?? null;
}

export interface EmptyContext {
  countryCode: string;
  /** The active tab, or `null` for "All". */
  stage: InquiryStage | null;
  /** True when a search term or an owner filter is narrowing the list. */
  filtered: boolean;
  /** Live counts, so the upstream sentence can say how many are waiting. */
  counts: Readonly<Record<InquiryStage, number>> | null;
  onClearFilters: () => void;
  /**
   * WEB TIER A · WT5 — was `onNewInquiry: () => void`, a callback that opened
   * the New Inquiry slide-over. The kill ruling made creation a ROUTE
   * (`inquiries/new`), and this file's own recorded rule then decides the
   * control's element: "Anything navigational must be a real anchor:
   * middle-click and copy-link-address" — so the CTA is a `<Link>` and the
   * caller hands it the href, keeping the URL contract in one place exactly
   * as `hrefForStage` already does.
   */
  newInquiryHref: string;
  /** Href for the upstream stage, built by the caller so the URL contract stays in one place. */
  hrefForStage: (stage: InquiryStage) => string;
  canCreate: boolean;
}

/**
 * L5 — which spot each branch draws.
 *
 * A map rather than three literals at the call sites, and EXPORTED rather than private,
 * because "which drawing goes with which emptiness" is the part that silently rots: it is
 * invisible to every text assertion, so a wrong one would ship a magnifying glass over a
 * country that has simply never been used. The map is the single definition and the test
 * reads this, not a copy of it.
 *
 * `column` — the fourth spot — is NOT used here. It draws a kanban column, and this file
 * renders the TABLE. A stage tab is a filtered list, not a column, and the board's own
 * empty column is the board's to draw.
 */
export const EMPTY_SPOTS: Readonly<Record<'filtered' | 'stage' | 'country', SpotName>> = {
  // A search that matched nothing. The magnifier over an empty lens says "the query ran
  // and came back empty", which is a different fact from "there is nothing here".
  filtered: 'search',
  // One stage of a pipeline that has rows elsewhere.
  stage: 'inquiries',
  // A country with no inquiries at all.
  country: 'inquiries',
};

/**
 * Pick and render the right empty state.
 *
 * The order of the branches is the order of the questions an operator would ask: "did my
 * filter do this?", then "is this column empty?", then "is there anything here at all?".
 */
export function InquiryEmptyState(context: EmptyContext): React.JSX.Element {
  const { stage, filtered, counts, onClearFilters, newInquiryHref, hrefForStage, canCreate } =
    context;

  if (filtered) {
    return (
      <InquiryEmptyCard
        spot={EMPTY_SPOTS.filtered}
        message="No inquiries match these filters."
        cta={
          <button type="button" onClick={onClearFilters} className={CTA_CLASSES}>
            Clear filters
          </button>
        }
      />
    );
  }

  if (stage !== null) {
    const upstream = upstreamStage(stage);
    const waiting = upstream === null ? 0 : (counts?.[upstream] ?? 0);
    if (upstream !== null && waiting > 0) {
      return (
        <InquiryEmptyCard
          spot={EMPTY_SPOTS.stage}
          message={`No inquiries in ${stageTabLabel(stage)} — follow up on ${String(waiting)} ${stageTabLabel(upstream)} ${waiting === 1 ? 'inquiry' : 'inquiries'}`}
          cta={
            <Link to={hrefForStage(upstream)} className={CTA_CLASSES}>
              View {stageTabLabel(upstream)}
            </Link>
          }
        />
      );
    }
    return (
      <InquiryEmptyCard
        spot={EMPTY_SPOTS.stage}
        message={`No inquiries in ${stageTabLabel(stage)}.`}
      />
    );
  }

  /*
   * COPY, hard rule 7. Was: "No inquiries yet. The pipeline starts with the first one."
   * with a button reading "New Inquiry".
   *
   * Two defects in one card. "New Inquiry" is Title Case and it is a NOUN — it names the
   * thing that would exist afterwards instead of saying what pressing it does, which is
   * the rule's "buttons say what they do". And the sentence describes the emptiness
   * without inviting anybody out of it. The button now reads "Log the first inquiry",
   * which is the rule's own worked example, and the sentence hands off to it.
   *
   * The label is deliberately NOT the header's "New Inquiry" even though both go to the
   * same route (WT5: `inquiries/new`): the header action is a standing action available
   * on a full pipeline, this one is the single next step out of an empty country. Same
   * destination, different sentence, and the difference is the whole point of an empty
   * state.
   */
  return (
    <InquiryEmptyCard
      spot={EMPTY_SPOTS.country}
      message="No inquiries yet — the pipeline starts with the first one."
      cta={
        canCreate ? (
          /* WT5 — a Link, because the destination is a route (see EmptyContext). */
          <Link to={newInquiryHref} className={CTA_CLASSES}>
            Log the first inquiry
          </Link>
        ) : undefined
      }
    />
  );
}
